<script setup lang="ts">
import { useStorage } from "@vueuse/core";
import { ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { Checklist, ChecklistListDto } from "~/api";
import ChecklistDefinitionSecurityEditor from "~/Compliance/components/ChecklistDefinitionSecurityEditor.vue";
import ChecklistDefinitionUsageViewer from "~/Compliance/components/ChecklistDefinitionUsageViewer.vue";
import ChecklistDetails from "~/Compliance/components/ChecklistDetails.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth, emptyGuid } from "~/utils/constants";

type mainType = Checklist;
type listItemType = ChecklistListDto;

const myInfoStore = useMyInfoStore();

const loading = ref(true);
const drawerOpen = defineModel<boolean>({ required: true });
const props = defineProps<{
  checklistDto: listItemType;
}>();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: mainType): void;
  (e: "added", value: mainType): void;
  (e: "deleted", value: mainType): void;
}>();

const isUsable = computed(() => {
  return checklist.value.guid !== emptyGuid && checklist.value.guid;
});

const activeChecklistTab = useStorage("activeChecklistTab", "general");

const checklist = ref<mainType>({
  guid: "",
  sysOrgGuid: "",
  entityDefinitionGuid: "",
  name: "",
});
const lastSavedJson = ref<string>("");

// const form = ref<FormDefinition>();
// const rendererKey = ref(0);

// watch(
//   () => form.value,
//   () => {
//     rendererKey.value++;
//   },
// );

const updateLastUpdated = () => {
  lastSavedJson.value = JSON.stringify(checklist.value);
};

/** 'true' if any changes have been made, and a save is needed */
const saveNeeded = computed(() => {
  const changed = JSON.stringify(checklist.value) !== lastSavedJson.value;

  return changed;
});

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
      })
      // If the user cancels
      .catch(() => {
        emit("closeDrawer");
      });
  } else {
    emit("closeDrawer");
  }
};

const handleNewItemAdded = (item: mainType) => {
  checklist.value = item;
  updateLastUpdated();
  emit("added", item);
};

const handleItemUpdated = (item: mainType) => {
  // passthrough
  checklist.value = item;
  updateLastUpdated();
  emit("updated", item);
};

const handleItemDeleted = (item: mainType) => {
  checklist.value = item;
  emit("deleted", item);
};

onMounted(async () => {
  const guidWanted = props.checklistDto.guid;

  if (guidWanted?.startsWith("new") || !guidWanted) {
    activeChecklistTab.value = "general";
    checklist.value = {
      // @ts-expect-error - we want the server to generate a new guid
      guid: undefined,
      sysOrgGuid: emptyGuid,
      name: "",
      itemsInChecklists: [],
    };

    updateLastUpdated();
    loading.value = false;
    return;
  }
  apiClient.checklistDefinition
    .getChecklistDefinition(guidWanted, myInfoStore.currentSysOrgGuid)
    .then((res) => {
      checklist.value = res;
      updateLastUpdated();
      loading.value = false;
    });

  console.log(checklist.value);
});
</script>

<template>
  <el-drawer
    v-if="checklist"
    :model-value="drawerOpen"
    :title="checklist.name || '\u00A0'"
    :before-close="handleClose"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
  >
    <div v-loading="loading" class="item-designer">
      <el-tabs v-model="activeChecklistTab" type="card" class="demo-tabs">
        <el-tab-pane label="General" name="general">
          <ChecklistDetails
            v-if="checklist"
            :key="checklist.guid"
            class="details"
            :model-value="checklist"
            @added="handleNewItemAdded"
            @updated="handleItemUpdated"
            @deleted="handleItemDeleted"
          />
        </el-tab-pane>
        <el-tab-pane v-if="isUsable" label="Security" name="security">
          <ChecklistDefinitionSecurityEditor
            v-if="checklist"
            class="security"
            :model-value="checklist"
          />
        </el-tab-pane>
        <el-tab-pane v-if="isUsable" label="Usage" name="usage" lazy>
          <ChecklistDefinitionUsageViewer
            v-if="checklist"
            class="details"
            :model-value="checklist"
          />
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>

<style lang="less" scoped>
.item-designer {
  .renderer {
    display: grid;
    grid-template-areas:
      "renderer-inner edit edit"
      "renderer-inner details security";
    grid-template-columns: 2fr 1fr 1fr;
    gap: 1em;
    border-left: 1px solid #ebeef5;

    table {
      width: 100%;
      td {
        padding: 0.5em;
        border-bottom: 1px solid #ebeef5;
      }
    }
  }
}
</style>
